import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Observable} from 'rxjs';
import {SelectItem} from 'primeng/api';

import {CommonTransformationService} from './common-transformation.service';
import {QueryResult} from '../classes/query-result';
import {
  Account,
  AccountMappingType,
  AccountType,
  AccountTypeName,
  ChargeCodeType,
  ChartOfAccount,
  ChartOfAccountInfo,
  GaapType,
  ItemType,
  LedgerAccount,
  LedgerAccountInfo,
  Transaction,
  TreeViewType
} from '..';
import {map} from "rxjs/operators";
import {plainToClass} from "class-transformer";


@Injectable()
export class AccountService {

  constructor(private http: HttpClient, private transform: CommonTransformationService) {
  }

  getAccountTypes(): Observable<QueryResult> {
    const url = `/finance/account-types`;
    return this.http.get<QueryResult>(url);
  }

  getAccountTypesAsDropdown(): Observable<SelectItem[]> {
    return this.getAccountTypes().pipe(map(res => {
      return this.transform.convertToDropDown("name", res.list, "id");
    }));
  }

  getAccountMappingTypeAsDropdown(): Observable<SelectItem[]> { // filter out non mapping type accounts
    return this.getAccountTypes().pipe(map(res => {
      let accountMappingTypes = [];
      const accountTypes = plainToClass(AccountType, res.list);
      accountTypes.forEach(at => {
        if (this.nameInAccountMappingType(at.name)) {
          accountMappingTypes.push(at);
        }
      });
      return this.transform.convertToDropDown("name", accountMappingTypes, "id");
    }));
  }

  getRequiredAccountTypes(itemType: ItemType, codeType: ChargeCodeType, transaction: Transaction): Observable<AccountMappingType[]> {
    let url = `/finance/account-mappings/required-item-mapping-for-transaction/${transaction}`;
    url = url + `?codeType=${codeType}`;
    url = url + `&itemType=${itemType}`;
    return this.http.get<AccountMappingType[]>(url);
  }

  private nameInAccountMappingType(accountName: string): boolean {
    for (let accountMappingType of Object.values(AccountMappingType)) {
      if (accountMappingType == accountName) {
        return true;
      }
    }
    return false;
  }

  searchAccount(searchString: string): Observable<QueryResult> {
    const url = '/finance/accounts/search?searchString=' + searchString;

    return this.http.get<QueryResult>(url);
  }

  searchAccountAsDropdown(searchString: string): Observable<SelectItem[]> {
    // once searchString is implemented, change the url to /finance/accounts/search?searchString=
    const url = '/finance/accounts/search?accountName=' + searchString;

    return this.http.get<any>(url).pipe(map(res => {
      return this.transform.convertToLongLabelDropDown(["accountNumber", "accountName"], res.list);
    }));
  }

  searchAccountById(id: number): Observable<any> {
    const url = `/finance/accounts/${id}`;

    return this.http.get<any>(url);
  }

  getRootAccountInfo(ledgerAccountId: number): Observable<ChartOfAccountInfo> {
    const url = `/finance/root-account/info?ledgerAccountId=${ledgerAccountId}`;
    return this.http.get<ChartOfAccountInfo>(url);
  }

  // FixMe jC: rel-1.3 sso-9548 this needs to return only active root accounts
  getRootAccountAsDropdown(onlyActiveAccounts: boolean, onlyFiscalYearAccounts: boolean): Observable<SelectItem[]> {
    let url = `/finance/root-accounts/info?`;
    if (onlyActiveAccounts) {
      url += `onlyActiveAccounts=true&`;
    }
    if (onlyFiscalYearAccounts) {
      url += `onlyFiscalYearAccounts=true`;
    }
    return this.http.get<Account[]>(url).pipe(map(res => {
      return this.transform.convertToLongLabelDropDown(["accountNumber", "accountName"], res);
    }));
  }

  // FixMe jC: rel-1.3 sso-9548 this needs to return only active root accounts
  getAccountById(accountId: number, onlyFiscalYearAccounts?: boolean): Observable<ChartOfAccount> {
    let url = `/finance/account/${accountId}`;
    if(onlyFiscalYearAccounts){
      url = url + `?onlyFiscalYearAccounts=true`;
    }
    return this.http.get<ChartOfAccount>(url);
  }

  getAccountByGaapType(gaapType: string): Observable<SelectItem[]> {
    const url = '/finance/gaap-type';
    return this.http.get<QueryResult>(`${url}/${gaapType}?`).pipe(map(res => {
      return this.transform.convertToDropDown("accountName", res.list);
    }));
  }

  getAccountGroups(mappingType?: string): Observable<QueryResult> {
    let url = `/finance/account-groups`;

    if (mappingType) {
      url += `?mappingType=${mappingType}`;
    }
    return this.http.get<QueryResult>(url);
  }

  getAccountGroupsAsDropDown(mappingType?: string, returnObjValue?: boolean): Observable<SelectItem[]> {
    if (returnObjValue) {
      return this.getAccountGroups(mappingType).pipe(map(res => {
        return this.transform.convertToDropDown("name", res.list);
      }));
    } else {
      return this.getAccountGroups(mappingType).pipe(map(res => {
        return this.transform.convertToDropDown("name", res.list, "id");
      }));
    }
  }

  getLedgerAccount(ledgerAccountId: number): Observable<LedgerAccount> {
    let url = `/accounting/ledger-accounts/${ledgerAccountId}`;
    return this.http.get<LedgerAccount>(url);
  }

  searchLedgerAccountInfo(searchString?: string, accountType?: AccountTypeName | AccountMappingType, gaapType?: GaapType, accountId?: number, onlyLeaf?: boolean, onlyFiscalYearAccounts?: boolean,
                          onlyActiveAccounts?: boolean): Observable<QueryResult> {
    let url = `/accounting/general-ledger-info/search?count=-1`;
    if (searchString) {
      url += `&searchString=${searchString}`;
    }
    if (accountType) {
      url += `&accountType=${accountType}`;
    }
    if (gaapType) {
      url += `&gaapType=${gaapType}`;
    }
    if (accountId) {
      url += `&accountId=${accountId}`;

    }
    if (onlyLeaf) {
      url += `&onlyLeaf=true`;
    }
    if (onlyFiscalYearAccounts) {
      url += `&onlyFiscalYearAccounts=true`;
    }
    if (onlyActiveAccounts) {
      url += `&active=true`;
    }
    return this.http.get<QueryResult>(url);
  }

  convertToLedgerAccountInfo(ledgerAccount: LedgerAccount): LedgerAccountInfo {
    let ledgerAccountInfo = new LedgerAccountInfo();
    ledgerAccountInfo.ledgerAccountId = ledgerAccount.id;
    ledgerAccountInfo.accountId = ledgerAccount.accountId;
    ledgerAccountInfo.accountName = ledgerAccount.accountName;
    ledgerAccountInfo.accountNumber = ledgerAccount.accountNumber;

    return ledgerAccountInfo;
  }

  convertToLedgerAccount(ledgerAccountInfo: LedgerAccountInfo): LedgerAccount {
    let ledgerAccount = new LedgerAccount();
    ledgerAccount.id = ledgerAccountInfo.ledgerAccountId;
    ledgerAccount.accountId = ledgerAccountInfo.accountId;
    ledgerAccount.accountName = ledgerAccountInfo.accountName;
    ledgerAccount.accountNumber = ledgerAccountInfo.accountNumber;

    return ledgerAccount;
  }

  getLedgerAccountsAsDropDown(searchString: string, accountType?: AccountTypeName | AccountMappingType, gaapType?: GaapType, accountId?: number, onlyLeaf?: boolean, onlyFiscalYearAccounts?: boolean,
                              onlyActiveAccounts?: boolean, returnObjValue?: boolean): Observable<SelectItem[]> {
    if (returnObjValue) {
      return this.searchLedgerAccountInfo(searchString, accountType, gaapType, accountId, onlyLeaf, onlyFiscalYearAccounts, onlyActiveAccounts).pipe(map(res => {
        return this.transform.convertToLongLabelDropDown(["accountNumber", "accountName"], res.list);
      }));
    } else {
      return this.searchLedgerAccountInfo(searchString, accountType, gaapType, accountId, onlyLeaf, onlyFiscalYearAccounts, onlyActiveAccounts).pipe(map(res => {
        return this.transform.convertToLongLabelDropDown(["accountNumber", "accountName"], res.list, "ledgerAccountId");
      }));
    }
  }
}
